import { Card } from "$root/widgets-architecture/layout/Card";
import { Text } from "@mdotm/shadcn/legacy/components";
import { renderNodeOrFn, Switch, type NodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import type { ReactNode } from "react";

/**
 * The chrome the workspace charts share: a bordered card with a title, the chart's own controls, an
 * overflow-menu slot, and the CALCULATING / NO_DATA / ERROR walls.
 *
 * Extracted from `MetricComparisonChart` so the stacked-column and frequency charts can
 * be siblings of the column chart rather than modes of it. What they share beyond this chrome is
 * selection, in `chart-selection`: the column and stacked charts both draw one bar per portfolio, so they
 * select identically (click, shift-click, band drag) and differ only in how a selected bar is drawn. The
 * frequency chart keeps its own band drag: its X axis is bins, so a band resolves through the bins'
 * members rather than onto ids. The drag-threshold stays the column chart's own — tuned to exactly two
 * series over a portfolio X axis and deliberately not generalized.
 *
 * All three do carry a navigator, and the two new types build its strip out of a hidden series of their own
 * rather than out of a plotted one, which is what {@link navigatorSourceClassName} marks; the config itself
 * is per chart, since only the chart knows what the strip should be a picture *of*.
 *
 * Note the column chart's Min / Max / Average header is meaningless for both new types, which is why
 * it lives in the caller's `title` rather than here.
 */

/**
 * Marks the plot group of a series that exists only to feed the navigator strip — the stacked and frequency
 * charts each add one, hidden in the plot, because Highcharts would otherwise build the strip out of a
 * plotted series that is not what either chart is a picture of. Highcharts copies a series' `className`
 * onto its group, so this is how such a series is told apart from a real one in the rendered SVG.
 */
export const navigatorSourceClassName = "navigator-source";

export type ChartHeight = "short" | "tall" | "full" | number;

export function chartHeightInPx(chartHeight: ChartHeight): number | string {
	switch (chartHeight) {
		case "short":
			return 280;
		case "tall":
			return 480;
		case "full":
			return "100%";
		default:
			return chartHeight;
	}
}

export type ChartCardShellProps = {
	id?: string;
	title: NodeOrFn;
	/** Rendered by the card under the header, above `children`. */
	subTitle?: NodeOrFn;
	/** The chart's own controls (sorting, the proposal highlight), drawn just before {@link actions}. */
	midActions?: NodeOrFn;
	/** The overflow menu. Last in the header, and the anchor everything else is aligned against. */
	actions?: NodeOrFn;
	/** @default false */
	fullHeight?: boolean;
	children: ReactNode;
};

/**
 * Title | controls | ⋮ — the controls go in the card's *action* slot, next to the overflow menu, rather
 * than in its mid one.
 *
 * The header is `justify-between`, which splits its slack *around* every slot it is given: a mid slot
 * therefore lands at an x that depends on the title's width, and the four charts' titles differ by design
 * (the column chart's carries Min/Max/Average, the others just a name). Three titles, three x's, and
 * a sort trigger that does not line up with the one in the chart above it. Right-aligned behind the
 * fixed-width overflow button there is nothing left to vary: every chart puts them at the same x.
 */
function ChartHeaderActions({ midActions, actions }: Pick<ChartCardShellProps, "midActions" | "actions">) {
	const renderedMidActions = renderNodeOrFn(midActions);
	return (
		<>
			{renderedMidActions}
			{renderNodeOrFn(actions)}
		</>
	);
}

export function ChartCardShell({
	id,
	title,
	subTitle,
	midActions,
	actions,
	fullHeight = false,
	children,
}: ChartCardShellProps): React.JSX.Element {
	return (
		<Card
			id={id}
			className={{
				"border rounded-sm": true,
				"h-full": fullHeight,
			}}
			style={{
				background: themeCSSVars.palette_N0,
				borderColor: themeCSSVars.palette_N100,
			}}
			title={title}
			subTitle={subTitle}
			actionHeader={<ChartHeaderActions midActions={midActions} actions={actions} />}
		>
			{children}
		</Card>
	);
}

/**
 * A chart's name, as every header draws it: in quotes.
 *
 * The quotes live here rather than in each chart's own header, which is where they used to be — five
 * identical copies in the charts and none in {@link ChartWall}, so a chart's title lost its quotes while
 * it was loading and got them back when the data arrived. One component, one answer, in both states.
 */
export function ChartTitle({ children }: { children: ReactNode }): React.JSX.Element {
	return (
		<Text type="Body/M/Bold" color={themeCSSVars.palette_N500}>
			{"“"}
			{children}
			{"”"}
		</Text>
	);
}

/**
 * A chart's name as the header draws it: a string in quotes, a node the caller already drew as it is.
 *
 * A title that is itself a control — the density chart's inline level picker — is not a citation, so it
 * wears no quotes; and it has to read the same in the chart and in the walls that stand in for it.
 */
export function chartTitleNode(title: ReactNode): ReactNode {
	return typeof title === "string" ? <ChartTitle>{title}</ChartTitle> : title;
}

export type ChartWallStatus = "CALCULATING" | "NO_DATA" | "ERROR" | "SUCCESS";

export const chartWallDescriptions: Record<Exclude<ChartWallStatus, "SUCCESS">, string> = {
	CALCULATING: "Loading metrics..",
	ERROR: "Try to reload the metrics",
	NO_DATA: "No Metrics To Show",
};

export type ChartWallProps = {
	status: Exclude<ChartWallStatus, "SUCCESS">;
	chartTitle: ReactNode;
	/** Rendered under the header, as on the chart this wall stands in for. */
	subTitle?: NodeOrFn;
	midActions?: NodeOrFn;
	actions?: NodeOrFn;
	/** @default 'tall' */
	chartHeight?: ChartHeight;
	/** Overrides {@link chartWallDescriptions} for the given status. */
	description?: string;
};

export function ChartWall({
	status,
	chartTitle,
	subTitle,
	midActions,
	actions,
	chartHeight = "tall",
	description,
}: ChartWallProps): React.JSX.Element {
	return (
		<Card
			className={{
				"border rounded-sm h-full": true,
			}}
			style={{
				background: themeCSSVars.palette_N0,
				borderColor: themeCSSVars.palette_N100,
			}}
			title={chartTitleNode(chartTitle)}
			subTitle={subTitle}
			// The wall wears the same header as the chart it stands in for, controls included: they keep
			// working while the data is loading or missing.
			actionHeader={<ChartHeaderActions midActions={midActions} actions={actions} />}
		>
			<div className="flex items-center justify-center" style={{ height: chartHeightInPx(chartHeight), width: "100%" }}>
				<span>{description ?? chartWallDescriptions[status]}</span>
			</div>
		</Card>
	);
}

/**
 * Renders `children` on SUCCESS and the matching {@link ChartWall} otherwise. Every chart view goes
 * through this so the three chart types cannot drift in how they present loading and error states.
 */
export function ChartWallSwitch({
	status,
	children,
	...wall
}: Omit<ChartWallProps, "status"> & {
	status: ChartWallStatus;
	children: () => React.JSX.Element;
}): React.JSX.Element {
	return (
		<Switch
			case={status}
			match={{
				CALCULATING: () => <ChartWall {...wall} status="CALCULATING" />,
				ERROR: () => <ChartWall {...wall} status="ERROR" />,
				NO_DATA: () => <ChartWall {...wall} status="NO_DATA" />,
				SUCCESS: children,
			}}
		/>
	);
}
